import React, { useState, useEffect, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { fmtDate, fmtPriceForSymbol } from './DcaUtils';
import { formatPrice, formatQty } from "@/lib/utils";
import { cx } from 'class-variance-authority';

interface TradeHistoryPanelProps {
  botId: number;
  symbol: string;
  leverage: number;
}

export const TradeHistoryPanel: React.FC<TradeHistoryPanelProps> = ({ botId, symbol, leverage }) => {
  const [trades, setTrades] = useState<any[]>([]);
  const [tradesTotal, setTradesTotal] = useState(0);
  const [tradesPage, setTradesPage] = useState(1);
  const [tradesPer] = useState(30);
  const [loading, setLoading] = useState(false);
  const [isGroupedByCycle, setIsGroupedByCycle] = useState(true);

  const isPrivate = false; // Assuming false for now, can be passed as prop if needed
  const curSym = symbol.endsWith('KRW') ? '₩' : '$';

  const fetchTrades = useCallback(async (tPage = 1, isSilent = false) => {
    if (!botId) return;
    if (!isSilent) setLoading(true);

    try {
      const res = await fetch(`/dca/${botId}/cycles?trades_page=${tPage}`);
      const data = await res.json();

      setTrades(data.trades || []);
      setTradesTotal(data.trades_total || 0);
      setTradesPage(data.trades_page || tPage);
    } catch { } finally {
      if (!isSilent) setLoading(false);
    }
  }, [botId]);

  useEffect(() => {
    fetchTrades(tradesPage);
  }, [botId, tradesPage, fetchTrades]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          <span className="hidden md:block text-[11px] font-black text-foreground uppercase tracking-widest">개별 체결 내역 (Trades)</span>
          <Badge variant="outline" className="bg-foreground/5 text-muted-foreground border-border text-[9px] font-bold h-4 px-1">{tradesTotal}건</Badge>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 bg-muted/20 px-2 py-1 rounded-lg border border-border/50">
            <Checkbox 
              id="group-by-cycle" 
              checked={isGroupedByCycle} 
              onCheckedChange={(checked) => setIsGroupedByCycle(!!checked)}
              className="w-3 h-3 border-muted-foreground/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            <Label htmlFor="group-by-cycle" className="text-[10px] font-bold text-muted-foreground cursor-pointer select-none">사이클별 묶기</Label>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchTrades(tradesPage)}
            disabled={loading}
            className="h-8 px-2 text-muted-foreground hover:text-foreground hover:bg-muted/30 rounded-lg transition-all"
          >
            <RefreshCw className={cx("w-3.5 h-3.5 mr-1.5", loading && "animate-spin")} />
            <span className="text-[10px] font-bold">새로고침</span>
          </Button>
        </div>
      </div>

      <div className={`overflow-x-auto rounded-2xl border border-border shadow-sm ${loading ? 'opacity-50' : ''}`}>
        <table className="w-full text-[10px] text-left">
          <thead className="bg-muted text-muted-foreground uppercase font-black tracking-wider text-[9px]">
            <tr>
              <th className="px-3 py-3">시간</th>
              <th className="px-3 py-3">타입</th>
              <th className="px-3 py-3">이유</th>
              <th className="px-3 py-3 text-right">수량</th>
              <th className="px-3 py-3 text-right">비용 (Cost)</th>
              <th className="px-3 py-3 text-right">PnL</th>
              <th className="px-3 py-3 text-right">수수료</th>
              <th className="px-3 py-3 text-right">체결가</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {trades.length === 0 ? (
              <tr><td colSpan={8} className="py-12 text-center text-muted-foreground font-bold">기록된 체결 내역이 없습니다.</td></tr>
            ) : (
              (() => {
                let prevCycleId: any = undefined;
                return trades.map((t: any) => {
                  const pnl = Number(t.pnl || 0);
                  const fee = Number(t.fee || 0);
                  const qty = Number(t.qty || 0);
                  const cost = (Number(t.price || 0) * qty) / Number(leverage || 1);
                  const isZeroPnl = Math.abs(pnl) < 1e-8;
                  const typeLower = t.type.toLowerCase();
                  const isLongTrade = typeLower.includes('long') || typeLower.includes('buy');
                  const isShortTrade = typeLower.includes('short') || typeLower.includes('sell');
                  const typeColor = isLongTrade ? 'text-success' : (isShortTrade ? 'text-danger' : 'text-foreground');
                  
                  const showHeader = isGroupedByCycle && (prevCycleId !== t.dca_cycle_id);
                  prevCycleId = t.dca_cycle_id;

                  return (
                    <React.Fragment key={t.id}>
                      {showHeader && (
                        <tr className="bg-muted/40 group/header">
                          <td colSpan={8} className="px-3 py-1.5">
                            <div className="flex items-center gap-2">
                              <div className="h-4 w-1 bg-primary/40 rounded-full" />
                              <span className="text-[9px] font-black text-foreground/70 uppercase tracking-tighter">
                                {t.dca_cycle_id ? `DCA 사이클 #${t.dca_cycle_id}` : '수동 / 개별 주문'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                      <tr className={cx("hover:bg-muted/30 transition-colors", t.dca_cycle_id ? "bg-background" : "bg-muted/5")}>
                        <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(t.created_at).slice(5)}</td>
                        <td className={`px-2 py-3 font-bold ${typeColor}`}>{t.type}</td>
                        <td className={`px-2 py-3 text-muted-foreground font-medium text-[9px]`}>
                          {t.reason?.includes('offset') ? '상계처리' : (t.reason?.includes('rebound') ? '덜어내기' : t.reason)}
                        </td>
                        <td className="px-2 py-3 text-right font-bold tabular-nums">{isPrivate ? '****' : formatQty(qty, 6)}</td>
                        <td className="px-2 py-3 text-right font-medium text-foreground/80 tabular-nums">{isPrivate ? '****' : fmtPriceForSymbol(cost, symbol)}</td>
                        <td className={`px-2 py-3 text-right font-black tabular-nums ${isZeroPnl ? 'text-muted-foreground' : ((t.type?.toLowerCase().includes('offset') || t.type?.toLowerCase().includes('rebound')) ? 'text-amber-500' : (pnl > 0 ? 'text-success' : 'text-danger'))}`}>
                          {isZeroPnl ? '-' : (isPrivate ? '****' : (pnl > 0 ? `+${fmtPriceForSymbol(pnl, symbol)}` : fmtPriceForSymbol(pnl, symbol)))}
                        </td>
                        <td className="px-2 py-3 text-right text-muted-foreground tabular-nums opacity-60">
                          {fee > 0 ? (isPrivate ? '****' : fmtPriceForSymbol(fee, symbol)) : '-'}
                        </td>
                        <td className="px-2 py-3 text-right tabular-nums font-bold">{isPrivate ? '****' : fmtPriceForSymbol(Number(t.price), symbol)}</td>
                      </tr>
                    </React.Fragment>
                  );
                });
              })()
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 컨트롤 */}
      {tradesTotal > tradesPer && (
        <div className="flex items-center justify-between px-2 py-2 bg-muted/10 rounded-xl">
          <div className="text-[10px] text-muted-foreground font-bold">
            총 {tradesTotal.toLocaleString()}건 중 {(tradesPage - 1) * tradesPer + 1}~{Math.min(tradesPage * tradesPer, tradesTotal)} 표시
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTradesPage(1)}
              disabled={tradesPage === 1 || loading}
              className="h-7 w-7 p-0 rounded-lg hover:bg-primary/10 hover:text-primary transition-all shadow-none border-border/50"
            >
              <ChevronLeft className="w-4 h-4 -ml-0.5" /><ChevronLeft className="w-4 h-4 -ml-2.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTradesPage((p: number) => Math.max(1, p - 1))}
              disabled={tradesPage === 1 || loading}
              className="h-7 w-7 p-0 rounded-lg hover:bg-primary/10 hover:text-primary transition-all shadow-none border-border/50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center justify-center h-7 px-3 bg-background border border-border/50 rounded-lg text-[10px] font-black text-primary">
              {tradesPage} / {Math.ceil(tradesTotal / tradesPer)}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTradesPage((p: number) => p + 1)}
              disabled={tradesPage >= Math.ceil(tradesTotal / tradesPer) || loading}
              className="h-7 w-7 p-0 rounded-lg hover:bg-primary/10 hover:text-primary transition-all shadow-none border-border/50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTradesPage(Math.ceil(tradesTotal / tradesPer))}
              disabled={tradesPage >= Math.ceil(tradesTotal / tradesPer) || loading}
              className="h-7 w-7 p-0 rounded-lg hover:bg-primary/10 hover:text-primary transition-all shadow-none border-border/50"
            >
              <ChevronRight className="w-4 h-4 -mr-2.5" /><ChevronRight className="w-4 h-4 -mr-0.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
